import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { OrgRecord } from "@/shared/domain/orgs/OrgRecord";

import { orgQueryKeys } from "./orgQueryKeys";

/**
 * Search orgs by name or ID. Requires admin privileges.
 * Only fires when the search string is at least 2 characters.
 *
 * TODO(pratik): fold into `useListOrgs` and delete this. It calls
 * `GET /v1/orgs/all`, which `GET /v1/orgs/search` (behind `useListOrgs`)
 * strictly supersedes — same `page_token` and `search`, plus `tier` and
 * `limit`, both admin-only, both paginated. `OrgAutocomplete` is the only
 * consumer; migrating it means taking `data.pages.flatMap((p) => p.items)`
 * instead of a flat array, then dropping this hook, `OrgService.searchOrgs`,
 * and `orgQueryKeys.search*`.
 */
export function useSearchOrgs(search: string) {
  const { orgService } = useDomainServices();
  const trimmed = search.trim();

  return useQuery<OrgRecord[]>({
    queryKey: orgQueryKeys.search(trimmed),
    queryFn: ({ signal }) =>
      orgService.searchOrgs(trimmed, { abortSignal: signal }),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}
